import {
    FormControl,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    useDisclosure,
    useMergeRefs,
    Text,
    Box,
    InputLeftElement
    //Progress
  } from "@chakra-ui/react"

  import { forwardRef, useRef, useState } from "react"
  import { HiEye, HiEyeOff } from "react-icons/hi"
  import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
  import { faLock } from "@fortawesome/free-solid-svg-icons"; 
  
  export const PasswordField = forwardRef(({ value, onChange, showStrengthBar = true, ...props }, ref) => {
    const { isOpen, onToggle } = useDisclosure()
    const inputRef = useRef(null)
    const [password, setPassword] = useState('');
    const [passwordStrength, setPasswordStrength] = useState(null);

  
    const mergeRef = useMergeRefs(inputRef, ref)
    const onClickReveal = () => {
      onToggle()
      if (inputRef.current) {
        inputRef.current.focus({ preventScroll: true })
      }
    }

    // Function to calculate password strength
  const getPasswordStrength = (password) => {
    const lengthCriteria = password.length >= 8; // Minimum length
    const uppercaseCriteria = /[A-Z]/.test(password); // Has uppercase letter
    const lowercaseCriteria = /[a-z]/.test(password); // Has lowercase letter
    const digitCriteria = /\d/.test(password); // Has digit
    const specialCharCriteria = /[!@#$%^&*(),.?":{}|<>]/.test(password); // Has special char

    let score = 0;

    // Check for criteria
    if (lengthCriteria) score += 1;
    if (uppercaseCriteria) score += 1;
    if (lowercaseCriteria) score += 1;
    if (digitCriteria) score += 1;
    if (specialCharCriteria) score += 1;

    return Math.min(score, 4);;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    onChange(e); // This passes the password back to the parent
    // Calculate password strength
    const strengthScore = getPasswordStrength(newPassword);
    setPasswordStrength(strengthScore);
  };

  const getStrengthLabel = () => {
    if (passwordStrength === null) return '';
    switch (passwordStrength) {
      // case 0:
      //   return 'Very Weak';
      case 1:
        return 'Weak';
      case 2:
        return 'Fair';
      case 3:
        return 'Good';
      case 4:
        return 'Strong';
      default:
        return '';
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength === null) return '';
    switch (passwordStrength) {
      // case 0:
      //   return 'red.500';
      case 1:
        return 'red.500';
      case 2:
        return 'yellow.500';
      case 3:
        return 'blue.500';
      case 4:
        return 'green.500';
      default:
        return '';
    }
  };

  // Calculate the progress bar value (percentage)
  const getStrengthPercentage = () => {
    switch (passwordStrength) {
      case 0: return 20;  // Very Weak
      case 1: return 25;  // Weak
      case 2: return 50;  // Fair
      case 3: return 75;  // Good
      case 4: return 100; // Strong
      default: return 0;  // No password
    }
  };

  const getRemainingColor = () => {
    if (passwordStrength === null) return 'gray.50';
    return 'gray.200';  // Color for the unfilled portion
  };
  
    return (
      <FormControl>
        <InputGroup>
        <InputLeftElement>
          <FontAwesomeIcon color="gray" icon={faLock} />
        </InputLeftElement>
          <Input
            ref={mergeRef}
            type={isOpen ? "text" : "password"}
            autoComplete="current-password"
            required
            value={password}
            onChange={handlePasswordChange}
            {...props}
          />
          <InputRightElement>
            <IconButton
              color="gray"
              variant="text"
              aria-label={isOpen ? "Mask password" : "Reveal password"}
              icon={isOpen ? <HiEyeOff /> : <HiEye />}
              onClick={onClickReveal}
            />
          </InputRightElement>
        </InputGroup>
         {/* Conditionally render strength bar */}
        {showStrengthBar && passwordStrength !== null && (
          <>
            <Box mt={2} h="8px" borderRadius="md" bg={getRemainingColor()}>
            <Box
              h="100%"
              width={`${getStrengthPercentage()}%`}
              bg={getStrengthColor()}
              borderRadius="md"
            />
          </Box>
            <Text mt={1} color={getStrengthColor()}>
              {getStrengthLabel()}
            </Text>
          </>
        )}
      </FormControl>
    )
  })
  
  PasswordField.displayName = "PasswordField"
  